import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { levelLabel } from "@/lib/levels";
import { fmtDateOnly, fmtDateTime } from "@/lib/datetime";
import RecordFormDialog from "@/components/RecordFormDialog";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import {
  ArrowLeft,
  Loader2,
  Award,
  GraduationCap,
  User,
  Phone,
  Mail,
  Wallet,
  HeartPulse,
  BookMarked,
  Repeat,
  StickyNote,
  Plus,
  CalendarDays,
  Camera,
  Pencil,
  Trash2,
} from "lucide-react";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-base font-medium text-foreground">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm text-foreground break-words whitespace-pre-wrap" data-testid={testid}>
        {value || "—"}
      </p>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="text-xs uppercase tracking-wide font-semibold">{label}</span>
      </div>
      <p className={`mt-2 font-heading text-2xl font-medium ${accent ? "text-primary" : "text-foreground"}`}>
        {value}
      </p>
    </div>
  );
}

function AmountBadge({ amount }) {
  const positive = amount >= 0;
  return (
    <span className={`font-medium tabular-nums ${positive ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
      {positive ? "+" : ""}
      {amount}
    </span>
  );
}

export default function StudentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [recordDialog, setRecordDialog] = useState({ open: false, record: null });
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [deleteStudentOpen, setDeleteStudentOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/teachers").then((r) => setTeachers(r.data.filter((t) => t.active))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/students/${id}/detail`);
      setData(res.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Failed to load student");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const submitAdjustment = async (e) => {
    e.preventDefault();
    if (saving) return;
    const amt = parseInt(amount, 10);
    if (!Number.isInteger(amt) || amt === 0) {
      toast.error("Enter a non-zero whole number (use a minus sign for deductions)");
      return;
    }
    if (!note.trim()) {
      toast.error("A note is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/students/${id}/point-adjustments`, { amount: amt, note: note.trim() });
      toast.success("Adjustment added");
      setAmount("");
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteStudent = async () => {
    try {
      const { data } = await api.delete(`/admin/students/${id}`);
      toast.success(
        `Deleted ${data.student_deleted} — ${data.records_deleted} records, ${data.adjustments_deleted} adjustments, ${data.edit_logs_deleted} edit logs removed`
      );
      navigate("/admin/students");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const confirmDeleteRecord = async () => {
    try {
      await api.delete(`/admin/records/${deleteRecord.id}`);
      toast.success("Record deleted");
      setDeleteRecord(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center mt-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl">
        <Link to="/admin/students" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Students
        </Link>
        <div className="mt-6 bg-card border border-border rounded-lg p-10 text-center text-muted-foreground" data-testid="student-detail-error">
          {error}
        </div>
      </div>
    );
  }

  const { student, records, adjustments, summary } = data;

  return (
    <div className="max-w-4xl" data-testid="student-detail-page">
      <Link
        to="/admin/students"
        data-testid="detail-back-link"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Students
      </Link>

      {/* Header */}
      <div className="mt-4 bg-card border border-border rounded-lg p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground" data-testid="detail-student-name">
            {student.full_name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="font-normal gap-1">
              <GraduationCap className="h-3 w-3" /> {levelLabel(student.level) || "No level"}
            </Badge>
            {student.active ? (
              <Badge className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))] text-white">Active</Badge>
            ) : (
              <Badge variant="secondary">Inactive</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild className="h-10" data-testid="detail-edit-link">
            <Link to="/admin/students">Edit in Students list</Link>
          </Button>
          <Button
            variant="destructive"
            className="h-10"
            onClick={() => setDeleteStudentOpen(true)}
            data-testid="delete-student-button"
          >
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete Student
          </Button>
        </div>
      </div>

      {/* Points summary */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Award} label="Total points" value={summary.total_points} accent testid="detail-total-points" />
        <StatCard icon={BookMarked} label="Record points" value={summary.record_points} testid="detail-record-points" />
        <StatCard icon={Wallet} label="Adjustments" value={summary.adjustment_points} testid="detail-adjustment-points" />
        <StatCard icon={CalendarDays} label="Records" value={summary.total_records} testid="detail-total-records" />
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Academic */}
        <Section icon={GraduationCap} title="Academic" testid="detail-academic">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Level" value={levelLabel(student.level)} testid="detail-level" />
            <Field label="Age" value={student.age != null ? String(student.age) : ""} testid="detail-age" />
            <Field label="Gender" value={student.gender} testid="detail-gender" />
            <Field label="Session" value={student.session} testid="detail-session" />
            <Field label="Enrollment date" value={student.enrollment_date ? fmtDateOnly(student.enrollment_date) : ""} testid="detail-enrollment" />
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Assigned teachers</p>
              <div className="mt-1 flex flex-wrap gap-1" data-testid="detail-teachers">
                {student.assigned_teachers?.length ? (
                  student.assigned_teachers.map((t) => (
                    <Badge key={t.id} variant="secondary" className="font-normal gap-1">
                      <User className="h-3 w-3" /> {t.full_name}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">Unassigned</span>
                )}
              </div>
            </div>
          </div>
        </Section>

        {/* Contact */}
        <Section icon={Phone} title="Guardian & contact" testid="detail-contact">
          <div className="space-y-3">
            <Field label="Guardian name" value={student.guardian_name} testid="detail-guardian-name" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Phone" value={student.guardian_phone} testid="detail-guardian-phone" />
              <Field label="Phone 2" value={student.guardian_phone_2} testid="detail-guardian-phone2" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Email" value={student.guardian_email} testid="detail-guardian-email" />
              <Field label="Email 2" value={student.guardian_email_2} testid="detail-guardian-email2" />
            </div>
          </div>
        </Section>

        {/* Financial */}
        <Section icon={Wallet} title="Financial (admin-only)" testid="detail-financial">
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-4">
              <Field
                label="Total fees"
                value={student.total_fees != null ? `$${Number(student.total_fees).toFixed(2)}` : ""}
                testid="detail-total-fees"
              />
              <Field
                label="Paid fees"
                value={student.paid_fees != null ? `$${Number(student.paid_fees).toFixed(2)}` : ""}
                testid="detail-paid-fees"
              />
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Balance</p>
                <p
                  className={`mt-1 text-sm font-medium ${
                    (Number(student.total_fees || 0) - Number(student.paid_fees || 0)) > 0
                      ? "text-destructive"
                      : "text-foreground"
                  }`}
                  data-testid="detail-balance"
                >
                  {student.total_fees != null || student.paid_fees != null
                    ? `$${(Number(student.total_fees || 0) - Number(student.paid_fees || 0)).toFixed(2)}`
                    : "—"}
                </p>
              </div>
            </div>
            <Field label="Admin notes" value={student.admin_notes} testid="detail-admin-notes" />
            <Field
              label="Payment date"
              value={student.payment_date ? fmtDateOnly(student.payment_date) : ""}
              testid="detail-payment-date"
            />
          </div>
        </Section>

        {/* Medical */}
        <Section icon={HeartPulse} title="Medical (admin-only)" testid="detail-medical">
          <Field label="Medical notes" value={student.medical_notes} testid="detail-medical-notes" />
        </Section>

        {/* Consent */}
        <Section icon={Camera} title="Consent (admin-only)" testid="detail-consent">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Social media photo consent</p>
            <div className="mt-1" data-testid="detail-photo-consent">
              {student.photo_consent == null ? (
                <span className="text-sm text-muted-foreground">Not set</span>
              ) : student.photo_consent ? (
                <Badge className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))] text-white">Yes</Badge>
              ) : (
                <Badge variant="secondary">No</Badge>
              )}
            </div>
          </div>
        </Section>
      </div>

      {/* Add point adjustment */}
      <div className="mt-4">
        <Section icon={Award} title="Add point adjustment" testid="detail-add-adjustment">
          <form onSubmit={submitAdjustment} className="flex flex-wrap gap-3 items-end" data-testid="detail-adjustment-form">
            <div className="space-y-1.5">
              <Label className="text-xs">Amount (use − for deductions)</Label>
              <Input
                type="number"
                className="h-11 w-40"
                placeholder="e.g. -50"
                data-testid="detail-adjustment-amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1.5 flex-1 min-w-[200px]">
              <Label className="text-xs">Note (required)</Label>
              <Input
                className="h-11"
                placeholder="Reason for adjustment"
                data-testid="detail-adjustment-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={saving} className="h-11" data-testid="detail-adjustment-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Add</>}
            </Button>
          </form>
        </Section>
      </div>

      {/* Records & points history */}
      <div className="mt-4">
        <Section icon={CalendarDays} title="Records & points" testid="detail-records-section">
          {adjustments.length > 0 && (
            <div className="mb-5">
              <p className="text-sm font-medium mb-2">Point adjustments</p>
              <div className="overflow-x-auto border border-border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead>By</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map((a) => (
                      <TableRow key={a.id} data-testid={`detail-adjustment-row-${a.id}`}>
                        <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDateTime(a.created_at)}</TableCell>
                        <TableCell><AmountBadge amount={a.amount} /></TableCell>
                        <TableCell className="text-muted-foreground max-w-xs truncate" title={a.note}>{a.note || "—"}</TableCell>
                        <TableCell className="text-muted-foreground">{a.admin_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">Daily records</p>
            <Button
              size="sm"
              className="h-9"
              onClick={() => setRecordDialog({ open: true, record: null })}
              data-testid="detail-add-record-button"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Add Record
            </Button>
          </div>
          {records.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground border border-border rounded-lg" data-testid="detail-no-records">
              No daily records yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="detail-records-list">
              {records.map((r) => (
                <div key={r.id} className="border border-border rounded-lg p-4" data-testid={`detail-record-${r.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="font-medium text-foreground">{fmtDateOnly(r.date)}</p>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-normal gap-1">
                        <Award className="h-3 w-3" /> {r.points} pts
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => setRecordDialog({ open: true, record: r })}
                        data-testid={`edit-record-${r.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-destructive hover:text-destructive"
                        onClick={() => setDeleteRecord(r)}
                        data-testid={`delete-record-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 space-y-2 text-sm">
                    {r.memorization && (
                      <p className="flex gap-2"><BookMarked className="h-4 w-4 mt-0.5 text-primary shrink-0" /><span><span className="font-medium">Memorization: </span><span className="text-muted-foreground">{r.memorization}</span></span></p>
                    )}
                    {r.review && (
                      <p className="flex gap-2"><Repeat className="h-4 w-4 mt-0.5 text-primary shrink-0" /><span><span className="font-medium">Review: </span><span className="text-muted-foreground">{r.review}</span></span></p>
                    )}
                    {r.other_notes && (
                      <p className="flex gap-2"><StickyNote className="h-4 w-4 mt-0.5 text-primary shrink-0" /><span><span className="font-medium">Notes: </span><span className="text-muted-foreground">{r.other_notes}</span></span></p>
                    )}
                    {!r.memorization && !r.review && !r.other_notes && (
                      <p className="text-muted-foreground">No progress notes for this day.</p>
                    )}
                  </div>
                  <p className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">Recorded by {r.recorded_by}</p>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <RecordFormDialog
        open={recordDialog.open}
        onOpenChange={(v) => setRecordDialog((s) => ({ ...s, open: v }))}
        studentId={id}
        record={recordDialog.record}
        admin
        teachers={teachers}
        defaultTeacherId={student.assigned_teachers?.[0]?.id || ""}
        onSaved={load}
      />

      <ConfirmDeleteDialog
        open={deleteStudentOpen}
        onOpenChange={setDeleteStudentOpen}
        title="Delete student?"
        confirmName={student.full_name}
        impact={`Delete ${student.full_name}? This will permanently remove ${summary.total_records} daily records, ${adjustments.length} point adjustments, and this student's profile. This cannot be undone.`}
        onConfirm={confirmDeleteStudent}
      />

      <ConfirmDeleteDialog
        open={!!deleteRecord}
        onOpenChange={(v) => !v && setDeleteRecord(null)}
        title="Delete record?"
        impact={deleteRecord ? `Delete the ${fmtDateOnly(deleteRecord.date)} record? Its edit history will also be removed. This cannot be undone.` : ""}
        onConfirm={confirmDeleteRecord}
      />
    </div>
  );
}
